import { IconLoader2 } from "@tabler/icons-react"
import { type FC, memo } from "react"

import { toDateTimeString } from "@forento/core/utilities/date"
import { tw } from "@forento/ui/utilities/tailwind"

import Link from "~/components/Link"
import { usePlatform, useUser } from "~/contexts/UserContext"
import { toRelativeDateString } from "~/utilities/date"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

import Button from "../Button"
import LoadingIndicator from "../LoadingIndicator"

const containerClass = tw`border-primary bg-primary/6 border-l-4`
const contentClass = tw`max-w-page-content flex items-center gap-4 px-8 py-4`
const bannerTextClass = tw`min-w-0 flex-1 text-sm leading-relaxed text-gray-700`
const bannerButtonClass = tw`bg-primary hover:bg-primary/90 inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium text-white transition-colors disabled:opacity-50`
const bannerSecondaryButtonClass = tw`text-primary hover:bg-primary/10 inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-md bg-white px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50`

const StatusBanner: FC = () => {
	const { user, signOut, reload } = useUser()
	const platform = usePlatform()

	const resendEmail = query.user.sendEmailVerification.useMutation({ onError: error => console.error(error) })
	const dismissLegacyEmailSender = query.platform.dismissLegacyEmailSender.useMutation({
		onError: error => console.error(error),
		onSuccess: () => reload(),
	})

	if (user?.isImpersonating) {
		return (
			<div className={containerClass}>
				<div className={contentClass}>
					<p className={bannerTextClass}>
						You are currently impersonating{" "}
						<strong>
							{user.firstName} {user.lastName}
						</strong>{" "}
						- <strong>{user.email}</strong>
					</p>
					<button type="button" onClick={signOut} className={bannerButtonClass}>
						Sign out
					</button>
				</div>
			</div>
		)
	}

	if (user !== undefined && !user.isEmailVerified) {
		return (
			<div className={containerClass}>
				<div className={contentClass}>
					<p className={bannerTextClass}>Your email address is not verified. Please check your inbox.</p>
					<button
						type="button"
						onClick={() => resendEmail.mutate()}
						disabled={resendEmail.isPending || resendEmail.isSuccess}
						className={bannerButtonClass}
					>
						{resendEmail.isSuccess ? (
							"Email sent"
						) : (
							<>
								{resendEmail.isPending && <LoadingIndicator size={12} color="white" />}
								Resend email
							</>
						)}
					</button>
				</div>
			</div>
		)
	}

	if (platform === null) return null

	if (platform.domain.isLegacyDnsHost) {
		return (
			<div className={containerClass}>
				<div className={contentClass}>
					<p className={bannerTextClass}>
						Your domain is using a legacy DNS host. This host will soon be removed, so please update your
						DNS settings in order to avoid any disruptions.
					</p>
					<Link to={routes.settings.domain.index()} className={bannerButtonClass}>
						Go to DNS settings
					</Link>
				</div>
			</div>
		)
	}

	if (platform.domain.hasUnconfiguredDnsRecords) {
		return (
			<div className={containerClass}>
				<div className={contentClass}>
					<p className={bannerTextClass}>
						Your selected custom domain is not fully connected. Configure the required DNS records to make
						your academy available on your custom domain.
					</p>
					<Link to={routes.settings.domain.index()} className={bannerButtonClass}>
						Configure DNS records
					</Link>
				</div>
			</div>
		)
	}

	if (platform.hasLegacyEmailSender) {
		return (
			<div className={containerClass}>
				<div className={contentClass}>
					<p className={bannerTextClass}>
						We have improved our email sender to improve deliverability. If you want to continue using the
						same custom email sender, please update it now.
					</p>
					<Link to={routes.settings.customizeAcademy()} className={bannerButtonClass}>
						Update email sender
					</Link>
					<button
						type="button"
						onClick={() => dismissLegacyEmailSender.mutate()}
						disabled={dismissLegacyEmailSender.isPending}
						className={bannerSecondaryButtonClass}
					>
						{dismissLegacyEmailSender.isPending && <IconLoader2 className="size-4 animate-spin" />}
						Use default email sender
					</button>
				</div>
			</div>
		)
	}

	if (platform.plan.type === "regular" && platform.plan.endDate) {
		return (
			<div className={containerClass}>
				<div className={contentClass}>
					<p className={bannerTextClass}>
						Automatic renewal is disabled. You will be downgraded to the free plan{" "}
						<span title={toDateTimeString(platform.plan.endDate)}>
							{toRelativeDateString(platform.plan.endDate)}
						</span>
						.
					</p>
					<Button variant="primary" onClick={routes.settings.billing.plans()} className={bannerButtonClass}>
						Go to plans
					</Button>
				</div>
			</div>
		)
	}

	if (
		platform.plan.type === "appsumo" ||
		platform.plan.type === "viededingue" ||
		platform.plan.type === "productcanyon"
	) {
		if (platform.plan.addon?.type === "trial") {
			return (
				<div className={containerClass}>
					<div className={contentClass}>
						<p className={bannerTextClass}>
							Your free Scale addon gift ends in {toRelativeDateString(platform.plan.addon.endDate)}. It
							ends automatically with no charge. Your lifetime plan stays yours. Keeping the addon
							features afterward is optional and requires a paid subscription.
						</p>
						<Link to={routes.settings.billing.plans()} className={bannerButtonClass}>
							View addon prices
						</Link>
					</div>
				</div>
			)
		}
	}

	return null
}

export default memo(StatusBanner)
